import React from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'motion/react';
import { Leaf, Heart, TreePine, Award, ArrowRight, CheckCircle, Phone, Shield, Clock } from 'lucide-react';
import { LeafCluster } from '../components/WoodlandSVG';
import {
  fadeUp, fadeLeft, fadeRight, scaleIn,
  staggerContainer, staggerItem, pageTransition, viewportConfig
} from '../components/AnimationVariants';

const team = [
  {
    name: 'Dr. Sowjanya Avireddi',
    title: 'Principal Dentist',
    photo: '/images/team-dr-sowjanya.png',
    bio: 'Dr. Sowjanya completed her BDS in India in 2008 and moved to the UK where she completed her registration exams. She gained over 7 years of hospital experience in Oral & Maxillofacial Surgery and Oral Medicine at leading NHS Foundation Trusts, including Guy\'s and St Thomas\' Hospital. An advocate of Minimally-Invasive Aesthetic Dentistry, she holds qualifications including LDS RCS Eng, MFDS RCS Edin, a Certificate in Implantology (VSS Academy–UCLan University), and is working towards a PG Diploma in Implant Dentistry.',
    specialties: ['Dental Implants', 'Minimally Invasive Aesthetics', 'Oral Surgery', 'ClearCorrect Aligners', 'Periodontology', 'Composite Bonding'],
  },
  {
    name: 'Dr. Millen',
    title: 'Specialist Orthodontist',
    photo: '/images/team-dr-millen.png',
    bio: 'Dr. Millen qualified in dentistry in Bristol in 1988 and worked in general practice for five years before specialising. She trained in orthodontics at Guy\'s Hospital, gaining both her Masters in Orthodontics and the Membership in Orthodontics from the Royal College of Surgeons in 2000, and attained the FDS RCS in 1995. Practice principal since 2004, she was one of the first orthodontists in the UK to hold a formal qualification in lingual orthodontics and offers patients the full range of modern orthodontic solutions.',
    specialties: ['Invisalign®', 'Lingual Braces', 'Fixed Braces', 'Adult Orthodontics', 'Children\'s Orthodontics', 'ClearCorrect®'],
  },
];

const values = [
  { icon: Heart, title: 'Comfort First', desc: 'Gentle care, clear explanations, and a relaxed environment — because how you feel matters as much as the outcome.' },
  { icon: Award, title: 'Clinical Precision', desc: 'Modern diagnostics, careful treatment planning, and a meticulous attention to detail underpin everything we do.' },
  { icon: Leaf, title: 'Minimal Intervention', desc: 'We preserve natural tooth structure wherever possible, choosing the most conservative approach that achieves excellent results.' },
  { icon: Shield, title: 'Honest Guidance', desc: 'Transparent options, clear fees, and no pressure — you will always know exactly what is recommended and why.' },
  { icon: Clock, title: 'Long-Term Health', desc: 'Treatment designed to support healthy smiles for years to come — not just today. We think ahead so you can smile with confidence long into the future.' },
];

const techFeatures = [
  {
    photo: '/images/equipment-axano-chair.png',
    label: 'AXANO Treatment Unit',
    sub: 'Dentsply Sirona',
    link: 'https://www.dentsplysirona.com/en-gb/discover/discover-by-brand/axano.html',
    desc: 'The AXANO dental unit from Dentsply Sirona sets the standard for patient comfort and clinical efficiency. Its ergonomic design, intuitive instrument delivery, and whisper-quiet operation create a calm, reassuring environment — helping nervous patients feel at ease while giving our clinicians precise, effortless control throughout every procedure.',
    highlights: ['Ergonomic patient positioning', 'Intuitive instrument delivery', 'Quiet, calm operation'],
  },
  {
    photo: '/images/equipment-cbct-axeos.png',
    label: 'Axeos CBCT 3D Imaging',
    sub: 'Dentsply Sirona',
    link: 'https://www.dentsplysirona.com/en-gb/discover/discover-by-brand/axeos.html',
    desc: 'The Axeos cone beam CT system delivers high-resolution 3D images of teeth, bone, nerves, and surrounding structures with ultra-low radiation doses. This level of diagnostic precision is essential for implant planning, complex extractions, and endodontic assessment — allowing us to plan treatment with complete confidence and communicate findings clearly to every patient.',
    highlights: ['3D cone beam CT imaging', 'Ultra-low radiation dose', 'Implant & endodontic planning'],
  },
  {
    photo: '/images/equipment-kaps900.jpg',
    label: 'Kaps 900 Dental Microscope',
    sub: 'Henry Schein',
    link: 'https://www.henryschein.co.uk/equipment/digital-equipment/microscopes/kaps-900',
    desc: 'The Kaps 900 surgical microscope provides exceptional magnification and illumination for microscope-assisted endodontics and precision restorative work. Working at this level of detail allows our clinicians to locate fine canals, identify cracks, and complete root treatments with an accuracy simply not possible with the naked eye — leading to better outcomes and greater tooth preservation.',
    highlights: ['High-magnification optics', 'Precision endodontic access', 'Greater tooth preservation'],
  },
  {
    photo: '/images/equipment-opus-system.png',
    label: 'OPUS® Air Polishing System',
    sub: 'Acteon Group',
    link: 'https://www.acteongroup.com/en/products/ultrasonics/conservation-ultrasonic-dentistry/opus/',
    desc: 'The OPUS® system combines ultrasonic scaling with advanced air polishing to remove plaque, biofilm, and staining gently and thoroughly — without the discomfort associated with traditional hygiene instruments. Its minimally invasive approach preserves enamel and soft tissues, making it particularly well-suited to patients with sensitivity, while delivering consistently excellent results at every visit.',
    highlights: ['Ultrasonic + air polishing', 'Gentle on enamel & gums', 'Ideal for sensitive patients'],
  },
];

export default function About() {
  return (
    <motion.div {...pageTransition} className="overflow-x-hidden pt-24">

      {/* Hero */}
      <section className="relative py-20 px-4 bg-gradient-to-br from-forest-dark to-forest overflow-hidden">
        <motion.div className="absolute right-0 top-0 w-64 opacity-15 hidden md:block"
          animate={{ y: [0, -10, 0] }} transition={{ duration: 6, repeat: Infinity, ease: 'easeInOut' }}>
          <LeafCluster />
        </motion.div>
        <div className="relative z-10 max-w-3xl mx-auto text-center">
          <motion.p className="text-sage-light text-sm uppercase tracking-widest font-medium mb-4 flex items-center justify-center gap-2"
            variants={fadeUp} initial="hidden" animate="visible" transition={{ delay: 0.1 }}>
            <Leaf className="w-4 h-4" /> About Us
          </motion.p>
          <motion.h1 className="text-4xl md:text-5xl font-serif text-white font-semibold mb-5"
            variants={fadeUp} initial="hidden" animate="visible" transition={{ delay: 0.2 }}>
            How Woodlands Dentistry Came to Be
          </motion.h1>
          <motion.p className="text-cream/75 text-lg leading-relaxed"
            variants={fadeUp} initial="hidden" animate="visible" transition={{ delay: 0.3 }}>
            The people, the values, and the long-standing commitment behind your care.
          </motion.p>
        </div>
      </section>

      {/* Our Story */}
      <section className="section-padding bg-cream">
        <div className="container-max">
          <div className="grid md:grid-cols-2 gap-14 items-center">
            <motion.div variants={fadeLeft} initial="hidden" whileInView="visible" viewport={viewportConfig}>
              <p className="section-subtitle"><Leaf className="w-4 h-4 text-sage" /> Our Story</p>
              <h2 className="section-title mb-5">Rooted in North Harrow for Over 25 Years</h2>
              <p className="text-gray-600 leading-relaxed mb-4">
                Woodlands Orthodontics is an established Specialist Orthodontic Practice on the Pinner and North Harrow border, where we have been providing the highest quality orthodontic treatment for adults and children for over 25 years.
              </p>
              <p className="text-gray-600 leading-relaxed mb-4">
                Woodlands Dentistry has grown from those trusted foundations, bringing together general dentistry, specialist orthodontics, restorative care, dental implants, cosmetic dentistry, facial aesthetics, and referral services — all in one reassuring setting.
              </p>
              <p className="text-gray-600 leading-relaxed mb-6">
                Whether you are visiting for a routine examination, a smile transformation, a dental emergency, or specialist treatment, our aim is to help you feel listened to, informed, and completely at ease from the moment you arrive.
              </p>
              <motion.div className="grid grid-cols-2 gap-4"
                variants={staggerContainer} initial="hidden" whileInView="visible" viewport={viewportConfig}>
                {['25+ Years in North Harrow', 'Specialist Orthodontics', 'Advanced Diagnostics', 'Caring, Personal Approach'].map((item) => (
                  <motion.div key={item} variants={staggerItem} className="flex items-center gap-2 text-sm text-gray-600">
                    <CheckCircle className="w-4 h-4 text-forest flex-shrink-0" />
                    {item}
                  </motion.div>
                ))}
              </motion.div>
            </motion.div>

            <motion.div variants={fadeRight} initial="hidden" whileInView="visible" viewport={viewportConfig}>
              <motion.div
                className="relative rounded-3xl overflow-hidden shadow-xl bg-gradient-to-br from-sage-pale to-cream-deeper"
                whileHover={{ scale: 1.02 }}
                transition={{ type: 'spring', stiffness: 200, damping: 20 }}
              >
                <div className="relative z-10 p-10 text-center">
                  <motion.div
                    className="w-20 h-20 rounded-full mx-auto mb-6 bg-gradient-to-br from-forest to-forest-light flex items-center justify-center shadow-lg"
                    whileHover={{ scale: 1.08 }}
                    transition={{ type: 'spring', stiffness: 300 }}
                  >
                    <Leaf className="w-10 h-10 text-cream" />
                  </motion.div>
                  <h3 className="font-serif text-forest text-2xl font-semibold mb-1">Woodlands Dentistry</h3>
                  <p className="text-brown-pale text-sm mb-6">106 Woodlands, North Harrow, HA2 6EW</p>
                  <div className="text-left space-y-3">
                    {[
                      'Specialist Orthodontic Practice since 1999',
                      'Expanded to full-service dentistry',
                      'Advanced digital diagnostics & CBCT imaging',
                      'Microscope-assisted endodontics',
                      'Implants, cosmetics & facial aesthetics',
                    ].map((cred, i) => (
                      <motion.div key={i} className="flex items-center gap-2 text-sm text-gray-600"
                        initial={{ opacity: 0, x: -10 }} whileInView={{ opacity: 1, x: 0 }}
                        transition={{ delay: i * 0.07 }} viewport={{ once: true }}>
                        <div className="w-1.5 h-1.5 rounded-full bg-gold flex-shrink-0" />
                        {cred}
                      </motion.div>
                    ))}
                  </div>
                </div>
              </motion.div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Values */}
      <section className="section-padding bg-white">
        <div className="container-max">
          <motion.div className="text-center mb-14" variants={fadeUp} initial="hidden" whileInView="visible" viewport={viewportConfig}>
            <p className="section-subtitle justify-center"><Heart className="w-4 h-4 text-sage" /> Our Values</p>
            <h2 className="section-title mb-4">What We Stand For</h2>
            <p className="text-gray-500 max-w-xl mx-auto">We believe excellent dentistry should feel clear, calm, and personal. Every patient deserves time to ask questions, understand their choices, and feel confident about the care they receive.</p>
          </motion.div>
          <motion.div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6"
            variants={staggerContainer} initial="hidden" whileInView="visible" viewport={viewportConfig}>
            {values.map((v, i) => {
              const Icon = v.icon;
              return (
                <motion.div key={i} variants={staggerItem}
                  className="bg-cream rounded-2xl p-7 border border-cream-deeper"
                  whileHover={{ scale: 1.02, boxShadow: '0 8px 24px rgba(45,90,61,0.08)' }}
                  transition={{ type: 'spring', stiffness: 300, damping: 20 }}
                >
                  <motion.div className="w-12 h-12 rounded-xl bg-forest mb-4 flex items-center justify-center"
                    whileHover={{ rotate: 10, scale: 1.1 }} transition={{ type: 'spring', stiffness: 400 }}>
                    <Icon className="w-6 h-6 text-cream" />
                  </motion.div>
                  <h3 className="font-serif text-forest text-xl font-semibold mb-2">{v.title}</h3>
                  <p className="text-gray-500 text-sm leading-relaxed">{v.desc}</p>
                </motion.div>
              );
            })}
          </motion.div>
        </div>
      </section>

      {/* Team */}
      <section className="section-padding bg-cream-dark">
        <div className="container-max">
          <motion.div className="text-center mb-14" variants={fadeUp} initial="hidden" whileInView="visible" viewport={viewportConfig}>
            <p className="section-subtitle justify-center"><Award className="w-4 h-4 text-sage" /> Our Team</p>
            <h2 className="section-title mb-4">Meet the Woodlands Family</h2>
            <p className="text-gray-500 max-w-xl mx-auto">Our clinicians combine long-standing local trust, specialist expertise, and a genuinely caring approach to every patient.</p>
          </motion.div>
          <motion.div className="flex flex-col gap-8 max-w-4xl mx-auto"
            variants={staggerContainer} initial="hidden" whileInView="visible" viewport={viewportConfig}>
            {team.map((member, i) => (
              <motion.div key={i} variants={staggerItem}
                className="card-wood overflow-hidden flex flex-col sm:flex-row"
                whileHover={{ y: -6, boxShadow: '0 20px 48px rgba(45,90,61,0.15)' }}
                transition={{ type: 'spring', stiffness: 300, damping: 20 }}
              >
                <div className="sm:w-56 sm:flex-shrink-0 aspect-[3/4] sm:aspect-auto overflow-hidden bg-cream-darker">
                  <img src={member.photo} alt={member.name}
                    className="w-full h-full object-cover object-top" loading="lazy" />
                </div>
                <div className="p-6 flex flex-col justify-center">
                  <h3 className="font-serif text-forest font-semibold text-xl leading-tight">{member.name}</h3>
                  <p className="text-brown-pale text-xs font-medium uppercase tracking-wide mb-3">{member.title}</p>
                  <p className="text-gray-500 text-sm leading-relaxed mb-4">{member.bio}</p>
                  <div className="flex flex-wrap gap-1.5">
                    {member.specialties.map((s, j) => (
                      <motion.span key={j} whileHover={{ scale: 1.05 }}
                        className="text-xs bg-sage-pale text-forest px-2.5 py-0.5 rounded-full font-medium">{s}
                      </motion.span>
                    ))}
                  </div>
                </div>
              </motion.div>
            ))}
          </motion.div>
        </div>
      </section>

      {/* Technology */}
      <section className="section-padding bg-white">
        <div className="container-max">
          <motion.div className="text-center mb-14" variants={fadeUp} initial="hidden" whileInView="visible" viewport={viewportConfig}>
            <p className="section-subtitle justify-center"><TreePine className="w-4 h-4 text-sage" /> Our Technology</p>
            <h2 className="section-title mb-4">Precision Equipment, Better Outcomes</h2>
            <p className="text-gray-500 max-w-xl mx-auto text-sm leading-relaxed">
              Our treatment rooms are equipped with technology that supports clearer diagnostics, more predictable outcomes, and a more comfortable experience at every visit.
            </p>
          </motion.div>

          <div className="space-y-16">
            {techFeatures.map((item, i) => {
              const isEven = i % 2 === 0;
              return (
                <motion.div key={i}
                  className={`grid md:grid-cols-2 gap-10 items-center`}
                  initial={{ opacity: 0, y: 30 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.55, ease: [0.22, 1, 0.36, 1] }}
                  viewport={{ once: true }}>

                  {/* Image */}
                  <motion.div
                    className={`rounded-3xl overflow-hidden bg-cream shadow-lg border border-cream-deeper ${isEven ? 'md:order-1' : 'md:order-2'}`}
                    whileHover={{ scale: 1.02 }}
                    transition={{ type: 'spring', stiffness: 200, damping: 20 }}>
                    <div className="h-72 md:h-80 flex items-center justify-center p-8 bg-gradient-to-br from-cream to-sage-pale/40">
                      <img src={item.photo} alt={item.label}
                        className="h-full w-full object-contain drop-shadow-md"
                        loading="lazy" />
                    </div>
                  </motion.div>

                  {/* Content */}
                  <div className={isEven ? 'md:order-2' : 'md:order-1'}>
                    <div className="inline-flex items-center gap-2 bg-sage-pale text-forest text-xs font-semibold px-3 py-1.5 rounded-full mb-4">
                      <TreePine className="w-3.5 h-3.5" />
                      {item.sub}
                    </div>
                    <h3 className="font-serif text-forest text-2xl md:text-3xl font-semibold mb-4 leading-tight">{item.label}</h3>
                    <p className="text-gray-600 leading-relaxed mb-6 text-sm md:text-base">{item.desc}</p>
                    <ul className="space-y-2.5 mb-6">
                      {item.highlights.map((h, j) => (
                        <li key={j} className="flex items-center gap-3 text-sm text-gray-700">
                          <CheckCircle className="w-4 h-4 text-forest flex-shrink-0" />
                          {h}
                        </li>
                      ))}
                    </ul>
                    <a href={item.link} target="_blank" rel="noopener noreferrer"
                      className="inline-flex items-center gap-2 text-sm font-semibold text-forest hover:text-gold transition-colors group">
                      Learn more about {item.label}
                      <ArrowRight className="w-4 h-4 transition-transform group-hover:translate-x-1" />
                    </a>
                  </div>
                </motion.div>
              );
            })}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-16 px-4 bg-gradient-to-r from-forest-dark to-forest text-center">
        <motion.div className="max-w-2xl mx-auto" variants={scaleIn} initial="hidden" whileInView="visible" viewport={viewportConfig}>
          <h2 className="text-3xl font-serif text-white font-semibold mb-4">Come Meet Our Team</h2>
          <p className="text-cream/75 mb-7">If you are looking for a practice that combines advanced care with a genuinely personal approach, we would love to welcome you.</p>
          <motion.div className="flex flex-col sm:flex-row gap-4 justify-center"
            whileHover={{ scale: 1.02 }} whileTap={{ scale: 0.97 }}>
            <Link to="/contact" className="btn-primary px-8 py-4 text-base">
              Book an Appointment <ArrowRight className="w-5 h-5" />
            </Link>
            <a href="tel:+442088665556" className="btn-outline-white px-8 py-4 text-base flex items-center justify-center gap-2">
              <Phone className="w-5 h-5" /> 020 8866 5556
            </a>
          </motion.div>
        </motion.div>
      </section>
    </motion.div>
  );
}
